import React, { useEffect } from 'react';
import { StyleSheet, View, Text, FlatList, ActivityIndicator, RefreshControl, Modal, TouchableOpacity as RNTouchableOpacity } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import ReanimatedSwipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import Reanimated, { useAnimatedStyle, interpolate, SharedValue, Extrapolation } from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { useNotificationsManager } from '@/hooks/notifications/useNotificationsManager';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { NotificationItem as NotificationType } from '@/services/universe/types';
import { TouchableOpacity } from 'react-native-gesture-handler';

export default function Notifications() {
    const {
        notifications,
        isLoading,
        isRefreshing,
        isLoadingMore,
        errorModal,
        handleRefresh,
        handleLoadMore,
        handleNotificationPress,
        handleDelete,
        loadNotifications,
        closeErrorModal,
        handleDeleteFromModal
    } = useNotificationsManager();

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);


    const RightActions = ({
        dragX,
        onDelete
    }: {
        dragX: SharedValue<number>,
        onDelete: () => void
    }) => {
        const animatedStyle = useAnimatedStyle(() => {
            const trans = interpolate(
                dragX.value,
                [-80, 0],
                [0, 80],
                Extrapolation.CLAMP
            );

            return {
                transform: [{ translateX: trans }]
            };
        });

        return (
            <TouchableOpacity 
                style={styles.deleteAction} 
                onPress={onDelete}
                activeOpacity={0.6}
            >
                <Reanimated.View style={[
                    styles.deleteActionContent,
                    animatedStyle
                ]}>
                    <Ionicons name="trash-outline" size={24} color="#EF4444" />
                    <Text style={styles.deleteText}>Delete</Text>
                </Reanimated.View>
            </TouchableOpacity>
        );
    };

    const renderItem = ({ item }: { item: NotificationType }) => (
        <ReanimatedSwipeable
            renderRightActions={(_progress, dragX) => (
                <RightActions dragX={dragX} onDelete={() => handleDelete(item.id)} />
            )}
            friction={2}
            rightThreshold={40}
        >
            <NotificationItem 
                item={item} 
                onPress={handleNotificationPress} 
                showDelete={false}
            />
        </ReanimatedSwipeable>
    );

    if (isLoading && !isRefreshing) {
        return (
            <View style={[styles.container, styles.centerContent]}>
                <ActivityIndicator size="large" color={Colors.brand} />
            </View>
        );
    }
    return (
        <View style={styles.container}>
            <StatusBar style="dark" />
            <FlatList
                data={notifications}
                renderItem={renderItem}
                keyExtractor={(item) => item.id}
                ItemSeparatorComponent={() => <View style={styles.separator} />}
                contentContainerStyle={styles.listContent}
                refreshControl={
                    <RefreshControl
                        refreshing={isRefreshing}
                        onRefresh={handleRefresh}
                        tintColor={Colors.brand}
                    />
                }
                onEndReached={handleLoadMore}
                onEndReachedThreshold={0.5}
                ListFooterComponent={() => (
                    isLoadingMore ? (
                        <View style={styles.footerLoader}>
                            <ActivityIndicator size="small" color={Colors.brand} />
                        </View>
                    ) : null
                )}
                ListEmptyComponent={
                    <View style={styles.centerContent}>
                        <Text style={styles.emptyText}>No notifications yet</Text>
                    </View>
                }
            />

            <Modal
                visible={errorModal.visible}
                transparent={true}
                animationType="fade"
                onRequestClose={closeErrorModal}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContainer}>
                        <View style={styles.modalHeader}>
                            <Ionicons name="alert-circle-outline" size={32} color="#EF4444" />
                            <Text style={styles.modalTitle}>Notification</Text>
                        </View>
                        <Text style={styles.modalMessage}>{errorModal.message}</Text>
                        <View style={styles.modalActions}>
                            <RNTouchableOpacity 
                                style={[styles.modalButton, styles.cancelButton]} 
                                onPress={closeErrorModal}
                            >
                                <Text style={styles.cancelButtonText}>Close</Text>
                            </RNTouchableOpacity>
                            <RNTouchableOpacity 
                                style={[styles.modalButton, styles.deleteButton]} 
                                onPress={handleDeleteFromModal}
                            >
                                <Ionicons name="trash-outline" size={18} color="white" />
                                <Text style={styles.deleteButtonText}>Delete</Text>
                            </RNTouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'white',
    },
    centerContent: {
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    footerLoader: {
        paddingVertical: 20,
        alignItems: 'center',
    },
    emptyText: {
        fontSize: 16,
        color: '#9CA3AF',
    },
    listContent: {
        flexGrow: 1,
    },
    separator: {
        height: 1,
        backgroundColor: '#F3F4F6',
    },
    deleteAction: {
        backgroundColor: '#FEF2F2', // Very light red
        justifyContent: 'center',
        alignItems: 'center',
        width: 80,
    },
    deleteActionContent: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    deleteText: {
        color: '#EF4444', // Red
        fontSize: 12,
        fontWeight: '600',
        marginTop: 4,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 16,
        padding: 24,
        width: '100%',
        maxWidth: 400,
        elevation: 5,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
    },
    modalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    modalTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        marginLeft: 12,
        color: '#111827',
    },
    modalMessage: {
        fontSize: 16,
        color: '#4B5563',
        marginBottom: 24,
        lineHeight: 24,
    },
    modalActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
    },
    modalButton: {
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 100,
    },
    cancelButton: {
        backgroundColor: '#F3F4F6',
    },
    cancelButtonText: {
        color: '#4B5563',
        fontWeight: '600',
    },
    deleteButton: {
        backgroundColor: '#EF4444',
    },
    deleteButtonText: {
        color: 'white',
        fontWeight: '600',
        marginLeft: 6,
    },
});